(() => {
  const drawer = document.getElementById('EluneBagDrawer');
  if (!drawer || !drawer.showModal) return;

  const section = 'elune-bag-drawer';
  const status = drawer.querySelector('.ebd-status');
  const errorPanel = drawer.querySelector('[data-bag-error]');
  let busy = false;
  let opener;
  let previousOverflow;
  let closeTimer;
  let visibilityFrame;
  let cartChanged = false;

  const setBusy = value => {
    busy = value;
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-bag-content] button').forEach(button => { button.disabled = value; });
  };

  const setStatus = (message = '', visible = false) => {
    status.textContent = message;
    status.dataset.visible = String(visible);
  };

  const showUnavailable = message => {
    drawer.classList.add('is-stale');
    errorPanel.hidden = false;
    errorPanel.querySelector('p').textContent = message;
    setStatus(message);
  };

  const render = html => {
    if (typeof html !== 'string' || !html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href') || '';
    if (/\/password(?:[?#]|$)/.test(canonical)) return false;
    const next = doc.querySelector('#EluneBagDrawer [data-bag-content]') || doc.querySelector('[data-bag-content]');
    const current = drawer.querySelector('[data-bag-content]');
    if (!next || !current || !next.hasAttribute('data-count')) return false;

    current.replaceWith(next);
    drawer.classList.remove('is-stale');
    errorPanel.hidden = true;
    const count = Number(next.dataset.count) || 0;
    document.querySelectorAll('.cart-utility .cart-count').forEach(node => { node.textContent = count; });
    document.dispatchEvent(new CustomEvent('elune:cart-updated', { detail: { count } }));
    return true;
  };

  const refresh = async () => {
    const cartUrl = new URL(drawer.dataset.cartUrl, location.origin);
    const attempts = [
      { url: new URL(cartUrl), json: true },
      { url: new URL(cartUrl), json: false, sectionOnly: true },
      { url: cartUrl, json: false }
    ];
    attempts[0].url.searchParams.set('sections', section);
    attempts[1].url.searchParams.set('section_id', section);

    for (const attempt of attempts) {
      try {
        const response = await fetch(attempt.url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) continue;
        if (attempt.json) {
          if (!response.headers.get('content-type')?.includes('application/json')) continue;
          const sections = await response.json();
          if (render(sections?.[section])) return true;
        } else if (render(await response.text())) {
          return true;
        }
      } catch (error) {
        console.warn('ELUNE bag refresh attempt failed', error);
      }
    }
    return false;
  };

  const close = () => {
    cancelAnimationFrame(visibilityFrame);
    drawer.classList.remove('is-visible');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      if (drawer.open) drawer.close();
    }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
  };

  drawer.addEventListener('close', () => {
    document.body.style.overflow = previousOverflow || '';
    opener?.focus({ preventScroll: true });
    if (cartChanged && location.pathname.replace(/\/$/, '') === drawer.dataset.cartUrl.replace(/\/$/, '')) location.reload();
  });
  drawer.addEventListener('cancel', event => { event.preventDefault(); close(); });
  drawer.addEventListener('click', event => {
    if (event.target.closest('[data-bag-close]')) close();
    if (event.target === drawer) {
      const bounds = drawer.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right) close();
    }
  });

  async function open() {
    clearTimeout(closeTimer);
    cancelAnimationFrame(visibilityFrame);
    if (!drawer.open) {
      opener = document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      drawer.showModal();

      drawer.querySelector('[data-bag-close]').focus({ preventScroll: true });
    }
    visibilityFrame = requestAnimationFrame(() => { if (drawer.open) drawer.classList.add('is-visible'); });
    if (busy) return false;
    setBusy(true);
    setStatus(drawer.dataset.loading);
    let loaded = false;
    try { loaded = await refresh(); }
    finally {
      setBusy(false);
      if (loaded) setStatus();
      else showUnavailable(drawer.dataset.loadError);
    }
    return loaded;
  }

  document.addEventListener('click', event => {
    const link = event.target.closest('a.cart-utility');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    open();
  });

  drawer.addEventListener('click', async event => {
    const button = event.target.closest('[data-bag-key]');
    if (!button || busy) return;
    const key = button.dataset.bagKey;
    const quantity = Number(button.dataset.bagQuantity);
    setBusy(true);
    setStatus(drawer.dataset.updating);
    try {
      const response = await fetch(drawer.dataset.changeUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [section], sections_url: drawer.dataset.cartUrl })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : drawer.dataset.actionError);
      cartChanged = true;
      const updated = render(result.sections?.[section]) || await refresh();
      if (updated) setStatus(drawer.dataset.updated);
      else showUnavailable(drawer.dataset.updateError);
    } catch (error) {
      setStatus(error.message || drawer.dataset.actionError, true);
    } finally {
      setBusy(false);
      const target = [...drawer.querySelectorAll('[data-bag-key]')].find(node => node.dataset.bagKey === key);
      (target || drawer.querySelector('[data-bag-close]')).focus({ preventScroll: true });
    }
  });

  window.EluneBagDrawer = { open };
})();
